import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Star, Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export const Stars = ({ n = 0, size = 11, className = "" }) => (
  <span className={`inline-flex items-center ${className}`} aria-label={`${n} stars`}>
    {Array.from({ length: Math.max(0, Math.min(5, n)) }).map((_, i) => <Star key={i} style={{ width: size, height: size }} className="fill-amber-400 text-amber-400" />)}
  </span>
);

export const LevelBadge = ({ name, stars, className = "" }) =>
  name ? (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-900 border border-amber-200 ${className}`} data-testid="level-badge">
      <Stars n={stars} size={10} />{name}
    </span>
  ) : null;

export const LevelsEditor = ({ open, onClose, levels, onSaved, t }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setRows(levels.map((l) => ({ ...l }))); }, [open, levels]);
  const slug = (name) => name.toLowerCase().replace(/[^a-z0-9а-яё]+/gi, "-").replace(/^-|-$/g, "") || `lvl-${Date.now()}`;
  const save = async () => {
    setBusy(true);
    try {
      const body = { levels: rows.filter((r) => r.name.trim()).map((r) => ({ id: r.id || slug(r.name), name: r.name.trim(), stars: r.stars })) };
      const r = await axios.put(`${API}/team/levels`, body);
      onSaved(r.data.levels); toast.success(t("team.saved")); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || t("team.saveError")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="text-black border-2 border-black" data-testid="levels-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Star className="w-5 h-5" /> {t("team.levelsTitle")}</DialogTitle></DialogHeader>
        <p className="text-xs text-gray-500 -mt-2">{t("team.levelsHint")}</p>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`level-row-${i}`}>
              <Input value={r.name} placeholder={t("team.levelName")} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} className="border-2 border-black h-9" data-testid={`level-name-${i}`} />
              <div className="flex items-center gap-0.5 shrink-0" data-testid={`level-stars-${i}`}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setRows(rows.map((x, j) => (j === i ? { ...x, stars: n } : x)))} className="p-0.5">
                    <Star className={`w-4 h-4 ${n <= r.stars ? "fill-amber-400 text-amber-400" : "text-gray-300"}`} />
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="p-1.5 text-red-600 hover:bg-red-50 rounded shrink-0" data-testid={`level-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {rows.length < 8 && (
            <Button type="button" variant="outline" size="sm" onClick={() => setRows([...rows, { id: "", name: "", stars: Math.min(5, rows.length + 1) }])} className="border-2 border-black font-semibold" data-testid="add-level-btn"><Plus className="w-4 h-4 mr-1" />{t("team.addLevel")}</Button>
          )}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} className="bg-black text-white font-semibold" data-testid="save-levels-btn">{t("team.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
